import { useApolloClient } from '@apollo/client/react/compiled'
import { onWsReconnect } from '@dolpheen/apollo/wsEvents'
import type { ConversationListItemFragment } from '@gql'
import {
  ConversationsDocument,
  MessagingConversationScope,
  MessagingMessageDirection,
  Permission,
} from '@gql'
import useNavigator from '@hooks/useNavigator'
import useUser from '@hooks/useUser'
import { hasPermission } from '@sections/auth/access'
import { CONVERSATIONS_PAGE_SIZE } from '@sections/messaging/constants'
import { useWindowTitleContext } from '@ui/WindowTitle/context'
import { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'

import { messagingMessages } from '../messages'
import { cancelInboxRefresh, requestInboxRefresh } from '../store/inboxRefresh'
import { canFireNotification, startNotificationCoordinator } from '../store/notificationLeader'
import {
  getLastReadSeq,
  setUnreadConversationCount,
  subscribeConversationRead,
} from '../store/unreadStore'
import { messagingConversationUrl } from '../urls'
import { useInboxChanged } from './useInboxChanged'
import { getLastInboundSeq, isConversationUnread, useUnreadConversationCount } from './useUnread'

// The variables the badge counts from: the operator's own first inbox page,
// shared verbatim with the workspace list so both read ONE cache entry — and,
// through the shared scheduler, one network request.
const BADGE_VARIABLES = {
  includeDismissed: false,
  limit: CONVERSATIONS_PAGE_SIZE,
  operatorId: undefined,
  scope: MessagingConversationScope.ASSIGNED,
}

/**
 * The app-wide "soft" unread feed: keeps the nav badge live from ANYWHERE in
 * the app, not only while the messaging inbox is mounted. Subscribes to the
 * operator's own ASSIGNED `inboxChanged` nudges (content-free), re-reads the
 * first inbox page on each nudge, and recomputes the persisted unread count
 * the badge renders from. Also:
 *
 * - fires a browser notification for a NEW inbound message while NO tab of the
 *   app is on screen (click focuses the tab and opens the thread). Exactly one
 *   tab alerts however many are open — see `store/notificationLeader`;
 * - mirrors the count into the window title, so a hidden tab reads "(3) …";
 * - reconciles on ws reconnect — nothing replays, the read is the truth.
 *
 * The count is a FLOOR: it is derived from one page of
 * `CONVERSATIONS_PAGE_SIZE`, the same limitation the inbox itself has (no
 * server-side read-state exists). The query shares cache identity with the
 * workspace inbox list, so a recompute also freshens the inbox.
 */
export function useGlobalUnreadFeed(): void {
  const intl = useIntl()
  const { user } = useUser()
  const client = useApolloClient()
  const navigate = useNavigator()
  const isEnabled = Boolean(hasPermission(Permission.USE_MESSAGING, user))

  // Latest known inbound seq per conversation — the "new since last look"
  // baseline for notifications. Seeded silently on the first read so a page
  // load never replays old messages as notifications.
  const seenInboundSeqRef = useRef<Map<string, number> | null>(null)

  // Effect-synced so the notification click handler (fires long after this
  // render) sees the live identities without re-minting the callbacks.
  const navigateRef = useRef(navigate)
  const intlRef = useRef(intl)
  useEffect(() => {
    navigateRef.current = navigate
    intlRef.current = intl
  })

  const notifyNewInbound = useCallback((conversation: ConversationListItemFragment) => {
    // `canFireNotification` replaces the old own-tab `document.hidden` check:
    // it also requires this tab to be the elected leader and NO tab of the app
    // to be on screen, so ten open tabs alert once, not ten times.
    if (
      typeof Notification === 'undefined' ||
      Notification.permission !== 'granted' ||
      !canFireNotification()
    ) {
      return
    }

    const last = conversation.lastMessage
    const title = conversation.customerName?.trim() || conversation.customerWaPhone.displayNumber
    // The preview is the customer's words only while the newest message IS
    // the customer's. A template button tap answered by the bot within the
    // same nudge, or an auto-replied out-of-hours message, previews OUR
    // reply — that must not be announced as theirs.
    const isPreviewInbound = last?.direction === MessagingMessageDirection.IN
    const notification = new Notification(title, {
      body:
        (isPreviewInbound ? last?.textPreview?.trim() : undefined) ||
        intlRef.current.formatMessage(messagingMessages.notificationNewMessage),
      // One live notification per thread — a newer message replaces it.
      tag: `dolpheen-conversation-${conversation.id}`,
    })
    notification.onclick = () => {
      window.focus()
      navigateRef.current(messagingConversationUrl(conversation.id))
      notification.close()
    }
  }, [])

  /** Recount the badge from whatever the cache already holds. */
  const recountFromCache = useCallback(() => {
    const data = client.readQuery({
      query: ConversationsDocument,
      variables: BADGE_VARIABLES,
    })
    const items = data?.conversations.items ?? []
    setUnreadConversationCount(items.filter(isConversationUnread).length)
    return items
  }, [client])

  const refresh = useCallback(async () => {
    try {
      // Ask the shared scheduler rather than fetching: it coalesces this with
      // the workspace's own refresh of the same list, and `ensure` guarantees
      // the badge's variables are read even when no list is on screen (the
      // operator is elsewhere in the app, or watching a different queue).
      await requestInboxRefresh(client, { ensure: BADGE_VARIABLES })

      const items = recountFromCache()
      const isFirstRead = seenInboundSeqRef.current === null
      const seen = (seenInboundSeqRef.current ??= new Map())
      for (const item of items) {
        // Keyed on the newest INBOUND seq, not on the preview's direction: a
        // template button tap that the bot answered before this read, or an
        // out-of-hours message the auto-reply followed, has an outbound
        // preview and still deserves the notification.
        const lastInboundSeq = getLastInboundSeq(item)
        if (lastInboundSeq === null) {
          continue
        }

        const previousSeq = seen.get(item.id)
        const isNew =
          !isFirstRead &&
          (previousSeq === undefined || lastInboundSeq > previousSeq) &&
          lastInboundSeq > getLastReadSeq(item.id)
        if (isNew) {
          notifyNewInbound(item)
        }

        seen.set(item.id, lastInboundSeq)
      }
    } catch {
      // Tolerated: the badge keeps its last known value; the next nudge or
      // reconnect retries.
    }
  }, [client, notifyNewInbound, recountFromCache])

  // The per-user nudge feed. Content-free by design — the recompute read is
  // what carries the facts.
  useInboxChanged(isEnabled ? MessagingConversationScope.ASSIGNED : null, undefined, refresh)

  // Reading an own-inbox thread changes the client-derived unread state but
  // does not produce an inbox nudge, so explicitly reconcile the sole badge
  // writer after that local state change. Cache-only: the server state did not
  // change, and this used to fire a whole extra list fetch per message read.
  useEffect(
    () => (isEnabled ? subscribeConversationRead(recountFromCache) : undefined),
    [isEnabled, recountFromCache],
  )

  useEffect(() => {
    if (!isEnabled) {
      return
    }

    // Ask once, lazily; 'denied' is final and 'granted' is silent.
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      void Notification.requestPermission()
    }

    void refresh()
    // Nothing replays across a socket outage — reconcile from the read.
    const unsubscribe = onWsReconnect(() => void refresh())
    // One tab of the app notifies for all of them.
    const stopCoordinator = startNotificationCoordinator()
    return () => {
      unsubscribe()
      stopCoordinator()
      cancelInboxRefresh()
    }
  }, [isEnabled, refresh])

  // Mirror the badge into the window title so a hidden tab still shows it.
  const unreadCount = useUnreadConversationCount()
  const windowTitle = useWindowTitleContext()
  const setTitleBadge = windowTitle?.setTitleBadge
  useEffect(() => {
    if (!setTitleBadge) {
      return
    }

    setTitleBadge(isEnabled ? unreadCount : 0)
    return () => setTitleBadge(0)
  }, [isEnabled, setTitleBadge, unreadCount])
}
